import Action from '@/gf/components/Action'
import ConfirmationModal from '@/gf/components/ConfirmationModal'
import UploadButton from '@/gf/components/UploadButton'
import useMsgs from '@/gf/hooks/useMsgs'
import { ArrowUpTrayIcon, DocumentIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline'
import { DateTime } from 'luxon'
import { useState } from 'react'
import {
  OrgMachineDocumentsDocument,
  useAddOrgMachineDocumentMutation,
  useDeleteOrgMachineDocumentMutation,
  useOrgMachineDocumentsQuery,
} from '../../../_gen/gql'
import useGqlClient from '../../../hooks/useGqlClient'

// MIME types for the file picker, extensions for drag-and-drop: UploadButton's drop filter
// matches on the extension only.
const allowedFileTypes = [
  'application/pdf',
  'image/*',
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.heic',
]

interface Props {
  orgMachineId: string
}

// Files a buyer keeps against one unit, such as a parts diagram (GEA-12141).
const Documents: React.FC<Props> = ({ orgMachineId }) => {
  const client = useGqlClient()
  const [_, msgs] = useMsgs()
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const refetchQueries = [{ query: OrgMachineDocumentsDocument, variables: { id: orgMachineId } }]

  const { data, loading, error, refetch } = useOrgMachineDocumentsQuery({
    variables: { id: orgMachineId },
    fetchPolicy: 'network-only',
    client,
  })
  const [addDocument] = useAddOrgMachineDocumentMutation({ client, refetchQueries })
  const [deleteDocument, { loading: deleting }] = useDeleteOrgMachineDocumentMutation({
    client,
    refetchQueries,
  })

  const documents = data?.orgMachine?.documents ?? []

  const onUploadComplete = async (files: { url: string }[]) => {
    try {
      for (const file of files) {
        await addDocument({ variables: { orgMachineId, url: file.url } })
      }
      msgs.add('Document saved', 'positive')
    } catch (err) {
      console.error(err)
      msgs.addUnknownError()
    } finally {
      setUploading(false)
    }
  }

  const onError = (error: string) => {
    msgs.add(error, 'negative')
    setUploading(false)
  }

  const onConfirmDelete = async () => {
    if (!deletingId) return

    try {
      await deleteDocument({ variables: { id: deletingId } })
      msgs.add('Document deleted', 'positive')
    } catch (err) {
      console.error(err)
      msgs.addUnknownError()
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="pt-6">
      <div className="flex items-center justify-between gap-x-4">
        <div>
          <h3 className="font-black text-xl text-slate-600">Documents</h3>
          <p className="text-sm text-slate-500">
            Parts diagrams, manuals and other files for this unit (PDF or image)
          </p>
        </div>

        <UploadButton
          allowedFileTypes={allowedFileTypes}
          onUploadStarted={() => setUploading(true)}
          onUploadComplete={onUploadComplete}
          onError={onError}
          uploadContext="buyer_org_machine_document"
          serverNamedKey
        >
          <Action.S performing={uploading} disabled={uploading}>
            <ArrowUpTrayIcon className="inline-block h-5 w-5" /> Upload
          </Action.S>
        </UploadButton>
      </div>

      {!loading && error && (
        <p className="mt-3 text-sm text-red-600">
          Documents could not be loaded.{' '}
          <button type="button" className="underline" onClick={() => refetch()}>
            Try again
          </button>
        </p>
      )}

      {!loading && !error && documents.length === 0 && (
        <p className="mt-3 text-sm text-slate-500 italic">No documents yet</p>
      )}

      {documents.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-200 border border-slate-200 rounded-lg">
          {documents.map((doc) => {
            const Icon = doc.contentType?.startsWith('image/') ? PhotoIcon : DocumentIcon

            return (
              <li key={doc.id} className="flex items-center gap-x-3 px-4 py-3">
                <Icon className="h-6 w-6 flex-shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate font-medium text-blue-600 hover:underline"
                  >
                    {doc.filename ?? 'Document'}
                  </a>
                  <p className="text-sm text-slate-500">
                    {doc.uploadedByName ? `${doc.uploadedByName} · ` : ''}
                    {doc.insertedAt.toLocaleString(DateTime.DATE_MED)}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-full inline-flex items-center justify-center h-8 w-8 hover:bg-slate-50"
                  onClick={() => setDeletingId(doc.id)}
                  aria-label={`Delete ${doc.filename ?? 'document'}`}
                >
                  <TrashIcon className="h-5 w-5 text-slate-400" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <ConfirmationModal
        open={deletingId !== null}
        onCancel={() => setDeletingId(null)}
        onConfirm={onConfirmDelete}
        performing={deleting}
        title="Delete document?"
        text="The file is removed from this unit for everyone in your organization."
      />
    </div>
  )
}

export default Documents
